// Per-device online indicator — mirrors the Flutter getStatus():
//   • green dot  → the device reported within the last 60s (online)
//   • red dot + "Nm" → offline; N = minutes since it last reported
// Source is the device's LastUpdatedDateTime (present on the list rows). When a
// live MQTT status arrives (pass it as `live`), the device just reported, so we
// treat it as seen "now". Re-evaluates on a timer so the minutes advance.

import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

function parseTs(raw) {
  if (!raw) return null;
  const t = Date.parse(String(raw).trim().replace(' ', 'T'));
  return Number.isNaN(t) ? null : t;
}

export default function DeviceStatusDot({ device, live }) {
  const raw = device && (device.lastUpdatedDateTime ?? device.LastUpdatedDateTime);
  const seenRef = useRef(parseTs(raw));
  const [, force] = useState(0);
  const tick = () => force((n) => n + 1);

  // A fresh row from the server → adopt its timestamp if newer.
  useEffect(() => {
    const t = parseTs(raw);
    if (t && (!seenRef.current || t > seenRef.current)) { seenRef.current = t; tick(); }
  }, [raw]);

  // A live MQTT message means the device reported just now.
  useEffect(() => {
    if (live) { seenRef.current = Date.now(); tick(); }
  }, [live]);

  // Keep the "minutes ago" fresh.
  useEffect(() => {
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, []);

  const seen = seenRef.current;
  if (!seen) return null; // unknown last-seen → show nothing

  const diff = Date.now() - seen;
  if (diff < 60000) {
    return <FiberManualRecordIcon sx={{ fontSize: 11, color: 'success.main' }} />;
  }
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }} title={`Last seen ${Math.floor(diff / 60000)} min ago`}>
      <FiberManualRecordIcon sx={{ fontSize: 11, color: 'error.main' }} />
      <Typography variant="caption" color="error.main" sx={{ lineHeight: 1, fontWeight: 700 }}>
        {Math.floor(diff / 60000)}m
      </Typography>
    </Box>
  );
}
